// Rich meal-logging dialog. Replaces the previous flat-text MealLogDialog
// with a structured experience built around the patient adding multiple
// food items one-by-one, each with its own quantity + (optional) per-item
// notes. Highlights:
//   - Search-as-you-type against the server food catalog with debounced
//     queries; default suggestions when the search box is empty (most
//     popular items per category).
//   - Add free-text items inline when the catalog doesn't have what the
//     patient ate.
//   - Camera / photo upload for the meal — multipart upload via
//     dietService.uploadDietPhoto, the resulting URL goes on the log entry.
//   - Per-meal note + quantity-summary fields.
//
// Submission shape matches DietLogRequest (entries-preferred, items as
// legacy backwards-compat) — see PatientDietService.log on the backend.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import SessionDialog from '../../sessions/components/SessionDialog'
import { GhostButton, PrimaryButton, Row, Stack } from '../../../layout/styledComponents'
import { searchFoodCatalog, uploadDietPhoto } from '../../../services/dietService'
import { dietaryPreferenceLabel } from '../../../services/preferencesService'
import { usePatient } from '../../../context/PatientContext'

const SearchRow = styled.div`
  position: relative;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  &:focus {
    outline: none;
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

const SuggestionList = styled.div`
  margin-top: 6px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 12px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  display: flex;
  flex-direction: column;
`

const SuggestionRow = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: inherit;
  border-bottom: 1px solid ${(p) => p.theme?.color?.borderSoft || 'rgba(31,41,55,0.06)'};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const SuggestionName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const SuggestionMeta = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const CategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 10px;
`

// Dietary-filter status pill — shown only when the patient has a stored
// preference. Reads either as "Showing vegetarian only · Show all" (filter
// active) or "Showing all foods · Match diet" (filter off). The toggle
// flips showAllDiets locally; never persists.
const DietBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 12px;
  background: ${(p) =>
    p.$active
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme?.color?.accent || '#6B9080'
        : p.theme?.color?.border || '#E6E0D6'};
  font-size: 13px;
  flex-wrap: wrap;
`

const DietBarText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(p) =>
    p.$active
      ? p.theme?.color?.accent || '#3F7D58'
      : p.theme?.color?.inkSoft || '#4B5563'};
  font-weight: 600;
`

const DietBarToggle = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
`

const CategoryChip = styled.button`
  appearance: none;
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme?.color?.primary || '#1F3A5F'
        : p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) =>
    p.$active
      ? p.theme?.color?.primarySoft || '#E8EEF6'
      : p.theme?.color?.bgRaised || '#fff'};
  color: ${(p) =>
    p.$active
      ? p.theme?.color?.primary || '#1F3A5F'
      : p.theme?.color?.inkSoft || '#4B5563'};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
`

const SelectedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SelectedRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  flex-wrap: wrap;
`

const SelectedDetail = styled.div`
  flex: 1;
  min-width: 0;
`

const SelectedName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const SelectedSub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const QuantityInput = styled.input`
  width: 110px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 13px;
`

const RemoveBtn = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  cursor: pointer;
  font-size: 16px;
  padding: 4px 6px;
  &:hover {
    color: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  }
`

const Field = styled.div`
  margin-top: 14px;
`

const FieldLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 6px;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
`

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
`

const PhotoStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const PhotoTile = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 12px;
  overflow: hidden;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

const PhotoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PhotoRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TotalChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.accentSoft || '#E1ECE7'};
  color: ${(p) => p.theme?.color?.accent || '#3F7D58'};
  font-size: 12px;
  font-weight: 600;
`

const ErrorText = styled.div`
  color: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  font-size: 12px;
  margin-top: 6px;
`

// Default category mapping by meal type — surfaces the most-relevant chips
// at the top of the picker for each meal slot.
const DEFAULT_CATEGORIES_BY_MEAL = {
  BREAKFAST: ['BREAKFAST', 'BREAD', 'DRINK', 'FRUIT'],
  LUNCH: ['MAIN', 'BREAD', 'SIDE', 'SNACK'],
  SNACK: ['SNACK', 'FRUIT', 'DRINK', 'SWEET'],
  DINNER: ['MAIN', 'BREAD', 'SIDE', 'SNACK'],
}

const CATEGORY_LABEL = {
  BREAKFAST: 'Breakfast',
  MAIN: 'Mains',
  BREAD: 'Bread / rice',
  SIDE: 'Sides',
  SNACK: 'Snacks',
  DRINK: 'Drinks',
  SWEET: 'Sweets',
  FRUIT: 'Fruits',
}

export default function FoodLogDialog({
  open,
  mealType,
  mealLabel,
  onClose,
  onSubmit,
}) {
  const { currentProfile } = usePatient()
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState([]) // { code?, displayName, quantity, approxCalories?, notes? }
  const [photos, setPhotos] = useState([]) // { url, fileId?, mimeType, sizeBytes }
  const [uploading, setUploading] = useState(false)
  const [photoError, setPhotoError] = useState(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // When the patient has a stored dietary preference, the catalog search
  // is filtered server-side. The "Show all" toggle lets them temporarily
  // see everything (e.g. logging an off-diet treat or a guest's meal); it
  // never persists — switching slots / closing the dialog resets it.
  const [showAllDiets, setShowAllDiets] = useState(false)
  const fileInputRef = useRef(null)
  const dietaryPreference = currentProfile?.dietaryPreference || null
  const dietFilterActive = !!dietaryPreference && !showAllDiets

  const categoriesForMeal = useMemo(
    () => DEFAULT_CATEGORIES_BY_MEAL[mealType] || ['MAIN', 'BREAD', 'SNACK', 'DRINK'],
    [mealType],
  )

  // Reset transient state whenever the dialog reopens (or meal slot
  // changes) so a stale query/selection from a prior meal doesn't bleed in.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveCategory(null)
      setSelected([])
      setPhotos([])
      setNote('')
      setError(null)
      setPhotoError(null)
      setShowAllDiets(false)
    }
  }, [open, mealType])

  // Debounced server search. 220ms is enough to feel responsive while
  // collapsing fast typing into one round trip.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setSearching(true)
        const results = await searchFoodCatalog(currentProfile, {
          q: query,
          category: activeCategory || undefined,
          limit: 20,
          allDiets: showAllDiets || undefined,
        })
        if (cancelled) return
        setSuggestions(results || [])
      } catch (e) {
        if (!cancelled) setSuggestions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 220)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query, activeCategory, currentProfile, showAllDiets])

  const totalCalories = useMemo(() => {
    let sum = 0
    let any = false
    for (const e of selected) {
      if (e.approxCalories != null) {
        sum += e.approxCalories
        any = true
      }
    }
    return any ? sum : null
  }, [selected])

  function pickFromCatalog(item) {
    setSelected((prev) => [
      ...prev,
      {
        code: item.code,
        displayName: item.displayName,
        quantity: item.defaultQuantityLabel || '',
        approxCalories: item.approxCalories ?? null,
        notes: '',
      },
    ])
    // Don't clear query — user often picks several similar items in a row.
  }

  function addFreeText() {
    const txt = query.trim()
    if (!txt) return
    setSelected((prev) => [
      ...prev,
      {
        code: null,
        displayName: txt,
        quantity: '',
        approxCalories: null,
        notes: '',
      },
    ])
    setQuery('')
  }

  function updateSelectedQuantity(idx, q) {
    setSelected((prev) => prev.map((e, i) => (i === idx ? { ...e, quantity: q } : e)))
  }

  function removeSelected(idx) {
    setSelected((prev) => prev.filter((_, i) => i !== idx))
  }

  async function handlePhotoChosen(e) {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setPhotoError(null)
    setUploading(true)
    try {
      for (const f of files) {
        const result = await uploadDietPhoto(currentProfile, f)
        if (result?.url) {
          setPhotos((prev) => [...prev, result])
        }
      }
    } catch (err) {
      setPhotoError('Photo upload failed. Try a smaller file or check your connection.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function removePhoto(idx) {
    setPhotos((prev) => prev.filter((_, i) => i !== idx))
  }

  const canSubmit = !busy && selected.length > 0

  const handleSave = useCallback(async () => {
    if (!canSubmit) return
    setError(null)
    setBusy(true)
    try {
      const photoUrl = photos[0]?.url || null
      const additionalPhotoUrls = photos.length > 1
        ? photos.slice(1).map((p) => p.url).filter(Boolean)
        : null
      await onSubmit({
        mealType,
        entries: selected.map((e) => ({
          code: e.code || null,
          displayName: e.displayName,
          quantity: e.quantity || null,
          approxCalories: e.approxCalories ?? null,
          notes: e.notes || null,
        })),
        // Mirror selected items into the legacy `items` field so older
        // backend / mock readers see something useful too.
        items: selected.map((e) => e.displayName),
        photoUrl,
        additionalPhotoUrls,
        note: note.trim() || null,
      })
      onClose()
    } catch (e) {
      setError('Could not save meal. Try again.')
    } finally {
      setBusy(false)
    }
  }, [canSubmit, mealType, note, onClose, onSubmit, photos, selected])

  return (
    <SessionDialog
      open={open}
      title={`Log ${mealLabel || 'meal'}`}
      subtitle="Pick from the suggestions, add free-text items, attach a photo of your plate."
      primaryLabel={selected.length > 1 ? `Save ${selected.length} items` : 'Save'}
      primaryDisabled={!canSubmit}
      primaryLoading={busy}
      onPrimary={handleSave}
      onClose={onClose}
    >
      <Stack $gap="14px">
        <SearchRow>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search foods (e.g. dosa, dal, fruit)…"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                e.preventDefault()
                addFreeText()
              }
            }}
            autoFocus
          />
        </SearchRow>

        {dietaryPreference ? (
          <DietBar $active={dietFilterActive}>
            <DietBarText $active={dietFilterActive}>
              {dietFilterActive ? '✓ ' : '⊘ '}
              {dietFilterActive
                ? `Showing ${dietaryPreferenceLabel(dietaryPreference).toLowerCase()} only`
                : `Showing all foods (off ${dietaryPreferenceLabel(dietaryPreference).toLowerCase()})`}
            </DietBarText>
            <DietBarToggle
              type="button"
              onClick={() => setShowAllDiets((v) => !v)}
              aria-pressed={showAllDiets}
            >
              {dietFilterActive ? 'Show all' : 'Match diet'}
            </DietBarToggle>
          </DietBar>
        ) : null}

        <CategoryChips>
          <CategoryChip
            type="button"
            $active={!activeCategory}
            onClick={() => setActiveCategory(null)}
          >
            All
          </CategoryChip>
          {categoriesForMeal.map((cat) => (
            <CategoryChip
              key={cat}
              type="button"
              $active={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            >
              {CATEGORY_LABEL[cat] || cat}
            </CategoryChip>
          ))}
        </CategoryChips>

        {(suggestions.length > 0 || searching) && (
          <SuggestionList>
            {searching && suggestions.length === 0 ? (
              <SuggestionRow as="div" disabled>
                <SuggestionName style={{ color: '#9CA3AF' }}>Searching…</SuggestionName>
              </SuggestionRow>
            ) : (
              suggestions.map((item) => (
                <SuggestionRow
                  key={item.code}
                  type="button"
                  onClick={() => pickFromCatalog(item)}
                >
                  <SelectedDetail>
                    <SuggestionName>{item.displayName}</SuggestionName>
                    <SuggestionMeta>
                      {item.defaultQuantityLabel || '—'}
                      {item.approxCalories != null
                        ? ` · ~${item.approxCalories} kcal`
                        : ''}
                      {item.tags && item.tags.length > 0
                        ? ' · ' + item.tags.slice(0, 2).join(', ').toLowerCase()
                        : ''}
                    </SuggestionMeta>
                  </SelectedDetail>
                  <span style={{ color: '#1F3A5F', fontSize: 18 }}>+</span>
                </SuggestionRow>
              ))
            )}
            {!searching && suggestions.length === 0 && query.trim() && (
              <SuggestionRow type="button" onClick={addFreeText}>
                <SelectedDetail>
                  <SuggestionName>Add &quot;{query}&quot; as a custom item</SuggestionName>
                  <SuggestionMeta>Free-text — your nutritionist will see it.</SuggestionMeta>
                </SelectedDetail>
                <span style={{ color: '#1F3A5F', fontSize: 18 }}>+</span>
              </SuggestionRow>
            )}
          </SuggestionList>
        )}

        {selected.length > 0 && (
          <Field>
            <Row $between $wrap>
              <FieldLabel as="div">What you ate ({selected.length})</FieldLabel>
              {totalCalories != null ? (
                <TotalChip>~{totalCalories} kcal</TotalChip>
              ) : null}
            </Row>
            <SelectedList>
              {selected.map((e, idx) => (
                <SelectedRow key={`${e.code || 'free'}_${idx}`}>
                  <SelectedDetail>
                    <SelectedName>{e.displayName}</SelectedName>
                    {e.approxCalories != null ? (
                      <SelectedSub>~{e.approxCalories} kcal at default qty</SelectedSub>
                    ) : null}
                  </SelectedDetail>
                  <QuantityInput
                    type="text"
                    value={e.quantity || ''}
                    onChange={(ev) => updateSelectedQuantity(idx, ev.target.value)}
                    placeholder="Qty"
                  />
                  <RemoveBtn
                    type="button"
                    aria-label={`Remove ${e.displayName}`}
                    onClick={() => removeSelected(idx)}
                  >
                    ✕
                  </RemoveBtn>
                </SelectedRow>
              ))}
            </SelectedList>
          </Field>
        )}

        <Field>
          <FieldLabel>Photo of the meal (optional)</FieldLabel>
          <PhotoStrip>
            {photos.map((p, idx) => (
              <PhotoTile key={p.fileId || p.url || idx}>
                <PhotoImg src={p.url} alt="" />
                <PhotoRemove
                  type="button"
                  aria-label="Remove photo"
                  onClick={() => removePhoto(idx)}
                >
                  ✕
                </PhotoRemove>
              </PhotoTile>
            ))}
            <GhostButton
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploading}
              style={{ padding: '8px 14px', fontSize: 13 }}
            >
              {uploading ? 'Uploading…' : photos.length === 0 ? '📷 Add photo' : '+ Add another'}
            </GhostButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhotoChosen}
              style={{ display: 'none' }}
            />
          </PhotoStrip>
          {photoError ? <ErrorText>{photoError}</ErrorText> : null}
        </Field>

        <Field>
          <FieldLabel>Notes (optional)</FieldLabel>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How did it feel? Anything off? Spicier than usual?"
          />
        </Field>

        {error ? <ErrorText>{error}</ErrorText> : null}
      </Stack>
    </SessionDialog>
  )
}
